import React, { useState, useRef, useEffect } from 'react';

/**
 * FieldEditor Component
 * 
 * Inline editor for custom field values.
 * Renders appropriate input based on field type.
 */
const FieldEditor = ({ field, fieldType, value, onChange, onClose }) => {
  const [localValue, setLocalValue] = useState(value ?? fieldType?.defaultValue ?? '');
  const inputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      // Select all text for text inputs
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, []);

  // Handle save
  const handleSave = () => {
    onChange(localValue);
  };

  // Handle key down
  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Handle blur
  const handleBlur = () => {
    handleSave();
  };

  // Prevent click propagation
  const handleClick = (e) => {
    e.stopPropagation();
  };

  // Render based on field type
  switch (field.type) {
    case 'text':
      return (
        <input
          ref={inputRef}
          type="text"
          className="custom-field-editor custom-field-editor--text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
          placeholder={fieldType?.placeholder || 'Enter text...'}
        />
      );

    case 'number':
      return (
        <input
          ref={inputRef}
          type="number"
          className="custom-field-editor custom-field-editor--number"
          value={localValue ?? ''}
          onChange={(e) => setLocalValue(e.target.value === '' ? null : Number(e.target.value))}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
          placeholder={fieldType?.placeholder || '0'}
        />
      );

    case 'checkbox':
      return (
        <button
          ref={inputRef}
          className={`custom-field-editor custom-field-editor--checkbox ${localValue ? 'custom-field-editor--checkbox-checked' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onChange(!localValue);
          }}
          onKeyDown={handleKeyDown}
        >
          {localValue ? '✓' : ''}
        </button>
      );

    case 'select':
      return (
        <select
          ref={inputRef}
          className="custom-field-editor custom-field-editor--select"
          value={localValue || ''}
          onChange={(e) => {
            const newValue = e.target.value || null;
            setLocalValue(newValue);
            onChange(newValue);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
        >
          <option value="">Select...</option>
          {field.options?.map(option => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      );

    case 'date':
      return (
        <input
          ref={inputRef}
          type="date"
          className="custom-field-editor custom-field-editor--date"
          value={localValue || ''}
          onChange={(e) => setLocalValue(e.target.value || null)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
        />
      );

    case 'url':
      return (
        <input
          ref={inputRef}
          type="url"
          className="custom-field-editor custom-field-editor--url"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
          placeholder={fieldType?.placeholder || 'https://...'}
        />
      );

    default:
      return (
        <input
          ref={inputRef}
          type="text"
          className="custom-field-editor"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={handleClick}
        />
      );
  }
};

export default FieldEditor;
